import React from 'react';
import type { LucideIcon } from 'lucide-react';

import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';

export interface RowActionProps {
  /** The glyph. Always 16px; RowAction owns the button size (32px `icon-sm`). */
  icon: LucideIcon;
  /** The verb, shown as the tooltip ("Edit", "Revoke", "Open"). */
  label: string;
  /**
   * The record the action applies to. The accessible name becomes
   * "{label} {subject}" ("Revoke FAU research"), so a screen-reader user
   * moving down an actions column hears which row each button acts on.
   */
  subject?: string;
  /** Overrides the accessible name when it is not "{label} {subject}". */
  'aria-label'?: string;
  onClick?: () => void;
  /** `destructive` renders the ghost-destructive variant. */
  tone?: 'default' | 'destructive';
  /** The action this button started is running: disabled, aria-busy, spinner in place of the glyph. */
  loading?: boolean;
  /** Disabled with no reason to give (prefer `disabledReason`). */
  disabled?: boolean;
  /**
   * Why the action is unavailable for this row (rung 3 of the unavailable-
   * actions ladder). Passed to Button's own `disabledReason`: the button stays
   * focusable with aria-disabled, the reason is its tooltip and description,
   * and DataTable's row-click guard skips it because it is still a <button>.
   */
  disabledReason?: string;
}

/**
 * The icon-only action in a DataTable row (DESIGN_SYSTEM.md §4). A ghost
 * 32px button with its label as a tooltip, so glyphs as close as Ban and Undo2
 * are never guessed at. Don't put bare icon Buttons in an actions column.
 */
export const RowAction: React.FC<RowActionProps> = ({
  icon: Icon,
  label,
  subject,
  'aria-label': ariaLabel,
  onClick,
  tone = 'default',
  loading = false,
  disabled = false,
  disabledReason,
}) => {
  const accessibleName = ariaLabel ?? (subject ? `${label} ${subject}` : label);

  if (disabledReason) {
    return (
      <Button
        type="button"
        variant={tone === 'destructive' ? 'ghost-destructive' : 'ghost'}
        size="icon-sm"
        aria-label={accessibleName}
        loading={loading}
        disabledReason={disabledReason}
      >
        <Icon aria-hidden="true" />
      </Button>
    );
  }

  const button = (
    <Button
      type="button"
      variant={tone === 'destructive' ? 'ghost-destructive' : 'ghost'}
      size="icon-sm"
      aria-label={accessibleName}
      loading={loading}
      disabled={disabled}
      onClick={onClick}
    >
      <Icon aria-hidden="true" />
    </Button>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
};
